import '@lib/i18n/i18n';
// FaceLandmarkerProvider restored at app root — moving it into CameraStep
// caused a race where the SVG selfie-mask didn't render during live
// camera preview (only appeared on the captured-photo screen). The
// memory/CPU cost of preloading MediaPipe globally is acceptable given
// the user reaches the camera step in basically every onboarding run.
import { FaceLandmarkerProvider } from '@providers/FaceLandmarkerProvider';
import PaymentConfigProvider from '@providers/PaymentConfigProvider';
import UserProvider from '@providers/UserProvider';
import * as Sentry from '@sentry/react';
import {
  browserSessionIntegration,
  browserTracingIntegration,
} from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';

import packageJson from '../package.json';

import { VersionBadge } from '@components/common/VersionBadge';
import { ConfigRedirectProvider } from '@providers/ConfigRedirectProvider';
import App from './App';
import { store } from './store';
import './styles/index.css';

const APP_RELEASE_VERSION = `${packageJson.name}@${packageJson.version}`;

console.log('App release:', APP_RELEASE_VERSION);

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    integrations: [browserTracingIntegration(), browserSessionIntegration()],
    tracesSampleRate: 0.5,
    sendDefaultPii: true,
    release: APP_RELEASE_VERSION,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'unknown',

    // Включаем отладочную информацию для sourcemaps
    debug: false,
    beforeSend(event: any, hint: any) {
      // Добавляем контекстную информацию для лучшей отладки
      if (event.exception) {
        const error = hint.originalException;
        console.error('Sentry capturing error:', error);

        if (
          error &&
          error.message &&
          error.message.includes(
            "undefined is not an object (evaluating 'window.webkit.messageHandlers",
          )
        ) {
          return null;
        }

        // Добавляем информацию о релизе в контекст
        event.contexts = event.contexts || {};
        event.contexts.app = {
          ...event.contexts.app,
          version: packageJson.version,
          build_time: new Date().toISOString(),
        };
      }
      return event;
    },
  });

  // Запускаем сессию для health tracking
  Sentry.captureSession();
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

root.render(
  <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>}>
    <ConfigRedirectProvider>
      <Provider store={store}>
        <UserProvider>
          <PaymentConfigProvider>
            <FaceLandmarkerProvider>
              <App />
              <VersionBadge />
            </FaceLandmarkerProvider>
          </PaymentConfigProvider>
        </UserProvider>
      </Provider>
    </ConfigRedirectProvider>
  </Sentry.ErrorBoundary>,
);
